import React, { useEffect, useState, useRef, useCallback } from "react";
import { Video, VideoOff, Mic, MicOff, PhoneOff, Plus, Sparkles, Loader2, Users, Clock, X, Calendar, ScreenShare, MessageSquare, Wand2, ImageIcon, Hand, Smile, ShieldCheck, VolumeX, Volume2, Circle, Square, Download, Search, CheckCircle2, UserPlus, ChevronDown, ChevronUp } from "lucide-react";
import { api } from "../api";
import { useAuth } from "../AuthContext";
import { useVirtualBackground } from "./useVirtualBackground";
import { toHandle } from "../utils/handle";

// Virtual background presets — Google Meet-style "Professional" gallery.
// Uses stable Unsplash CDN URLs (auto-format/webp + fixed 1280×720 crop) so the
// images load fast and match every meeting's aspect ratio out of the box.
const _UP = (id) => `https://images.unsplash.com/${id}?auto=format&fit=crop&w=1280&h=720&q=80`;
const BG_PRESETS = [
  // Special (top row) — matches Google Meet's "None / Blur" cluster.
  { id: "none",       label: "None",           type: "none",    category: "Effects" },
  { id: "blur-light", label: "Slight blur",    type: "blur",    category: "Effects", intensity: 8 },
  { id: "blur",       label: "Blur",           type: "blur",    category: "Effects", intensity: 18 },

  // Professional — realistic office / home-office / studio backgrounds.
  { id: "library",     label: "Library",         type: "image", category: "Professional", src: _UP("photo-1507842217343-583bb7270b66") },
  { id: "shelves",     label: "Bookshelves",     type: "image", category: "Professional", src: _UP("photo-1519682337058-a94d519337bc") },
  { id: "minimal",     label: "Minimalist",      type: "image", category: "Professional", src: _UP("photo-1493663284031-b7e3aefcae8e") },
  { id: "wood",        label: "Wood wall",       type: "image", category: "Professional", src: _UP("photo-1524758631624-e2822e304c36") },
  { id: "office",      label: "Modern office",   type: "image", category: "Professional", src: _UP("photo-1497366216548-37526070297c") },
  { id: "workspace",   label: "Workspace",       type: "image", category: "Professional", src: _UP("photo-1497366811353-6870744d04b2") },
  { id: "loft",        label: "Loft window",     type: "image", category: "Professional", src: _UP("photo-1524758631624-e2822e304c36") },
  { id: "livingroom",  label: "Living room",     type: "image", category: "Professional", src: _UP("photo-1560448204-e02f11c3d0e2") },
  { id: "studio",      label: "Studio",          type: "image", category: "Professional", src: _UP("photo-1497366754035-f200968a6e72") },
  { id: "plants",      label: "Plants",          type: "image", category: "Professional", src: _UP("photo-1545239351-1141bd82e8a6") },
  { id: "meetroom",    label: "Meeting room",    type: "image", category: "Professional", src: _UP("photo-1517502884422-41eaead166d4") },
  { id: "boardroom",   label: "Boardroom",       type: "image", category: "Professional", src: _UP("photo-1521737604893-d14cc237f11d") },
  { id: "cafe",        label: "Café",            type: "image", category: "Professional", src: _UP("photo-1554118811-1e0d58224f24") },
  { id: "warm-home",   label: "Warm home",       type: "image", category: "Professional", src: _UP("photo-1616486338812-3dadae4b4ace") },
  { id: "concrete",    label: "Concrete",        type: "image", category: "Professional", src: _UP("photo-1487958449943-2429e8be8625") },
  { id: "brick",       label: "Brick wall",      type: "image", category: "Professional", src: _UP("photo-1558618666-fcd25c85cd64") },
  { id: "coastal",     label: "Coastal view",    type: "image", category: "Professional", src: _UP("photo-1507525428034-b723cf961d3e") },
  { id: "skyline",     label: "City skyline",    type: "image", category: "Professional", src: _UP("photo-1477959858617-67f85cf4f1df") },
  { id: "night-city",  label: "Night city",      type: "image", category: "Professional", src: _UP("photo-1519501025264-65ba15a82390") },
];

const MEETING_TYPES = [
  { id: "1on1", label: "1:1", desc: "Quick one-on-one sync" },
  { id: "team", label: "Team Standup", desc: "Daily team check-in" },
  { id: "client", label: "Client Call", desc: "External / customer call" },
  { id: "interview", label: "Interview", desc: "Hiring conversation" },
  { id: "webinar", label: "Webinar", desc: "Broadcast / many viewers" },
  { id: "brainstorm", label: "Brainstorm", desc: "Open creative session" },
];

export default function Meetings({ meetingId } = {}) {
  const [view, setView] = useState("list"); // list | room
  const [meetings, setMeetings] = useState([]);
  const [active, setActive] = useState(null); // current room meeting
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    const { data } = await api.get("/meetings");
    setMeetings(data);
    return data;
  };

  useEffect(() => {
    (async () => {
      const data = await load();
      // Auto-join if a specific meetingId was passed in (e.g. from the
      // "Join Now" popup in MeetingInvitationsWatcher).
      if (meetingId) {
        const m = data.find(x => x.id === meetingId);
        if (m) { setActive(m); setView("room"); }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingId]);

  const join = (m) => { setActive(m); setView("room"); };

  return (
    <div className="h-full flex flex-col" data-testid="app-meetings">
      {view === "list" && (
        <MeetingsList
          meetings={meetings}
          onCreate={() => setShowCreate(true)}
          onJoin={join}
          onRefresh={load}
        />
      )}
      {view === "room" && active && (
        <MeetingRoom
          meeting={active}
          onLeave={() => { setView("list"); load(); }}
        />
      )}
      {showCreate && (
        <CreateMeeting
          onClose={() => setShowCreate(false)}
          onCreated={async (m) => { setShowCreate(false); await load(); join(m); }}
        />
      )}
    </div>
  );
}

function MeetingsList({ meetings, onCreate, onJoin, onRefresh }) {
  return (
    <div className="flex-1 flex flex-col">
      <div className="flex items-center justify-between p-3 border-b border-white/10">
        <div className="flex items-center gap-2">
          <Video size={14} color="#ff7a2e" />
          <span className="text-sm font-medium">HEXA Meetings</span>
          <span className="text-[10px] text-white/40">Zoom-style · AI-powered</span>
        </div>
        <button data-testid="meeting-new" onClick={onCreate} className="btn-accent text-xs flex items-center gap-1">
          <Plus size={12} /> New Meeting
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 content-start">
        {meetings.length === 0 && (
          <div className="col-span-full text-center py-16 text-white/40 text-sm">
            <Video size={32} className="mx-auto mb-3" />
            No meetings yet. Schedule one with AI-generated agenda.
          </div>
        )}
        {meetings.map(m => (
          <div key={m.id} data-testid={`meeting-card-${m.id}`} className="app-grid-cell flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium tracking-tight">{m.title}</div>
              <span className={`text-[9px] px-2 py-0.5 rounded-full border ${
                m.status === "live" ? "bg-red-50 text-red-600 border-red-200" :
                m.status === "ended" ? "bg-slate-100 text-slate-600 border-slate-200" :
                "bg-orange-50 text-orange-600 border-orange-200"
              }`}>{m.status}</span>
            </div>
            <div className="text-[10px] text-white/50 flex items-center gap-2">
              <Calendar size={10} /> {new Date(m.scheduled_at).toLocaleString()}
              <Clock size={10} /> {m.duration_min}m
            </div>
            <div className="text-[10px] text-white/50 flex items-center gap-1">
              <Users size={10} /> {m.participants?.length || 0} participants
            </div>
            <div className="text-[10px] mono text-white/40">Room: {m.room_id}</div>
            <div className="flex gap-1 mt-1">
              <button onClick={() => onJoin(m)} data-testid={`meeting-join-${m.id}`} className="btn-accent text-xs flex-1">Join</button>
              <button
                onClick={async () => { await api.delete(`/meetings/${m.id}`); onRefresh(); }}
                className="btn-ghost text-xs"
              ><X size={11} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CreateMeeting({ onClose, onCreated }) {
  const { user } = useAuth();
  const [type, setType] = useState("1on1");
  const [title, setTitle] = useState("");
  const [when, setWhen] = useState(() => new Date(Date.now() + 5*60_000).toISOString().slice(0, 16));
  const [duration, setDuration] = useState(30);
  const [agenda, setAgenda] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [invitedNote, setInvitedNote] = useState("");

  // Participant picker
  const [selected, setSelected] = useState([]);    // [{address,name,username}]
  const [pSearch, setPSearch] = useState("");
  const [directory, setDirectory] = useState([]);
  const [showDrop, setShowDrop] = useState(false);
  const pickerRef = useRef(null);

  useEffect(() => {
    api.get("/users/directory").then(r => setDirectory(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    const h = (e) => { if (pickerRef.current && !pickerRef.current.contains(e.target)) setShowDrop(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const filtered = (pSearch.trim()
    ? directory.filter(u => {
        const q = pSearch.replace(/^@/, "").toLowerCase();
        return (u.address||"").toLowerCase().includes(q) || (u.name||"").toLowerCase().includes(q) || (u.username||"").toLowerCase().includes(q);
      })
    : directory
  ).filter(u => !selected.find(s => s.address === u.address));

  const addUser = (u) => { setSelected(p => [...p, u]); setPSearch(""); setShowDrop(false); };
  const removeUser = (addr) => setSelected(p => p.filter(s => s.address !== addr));

  const onKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const q = pSearch.trim();
    if (!q) return;
    if (q.includes("@") && !q.startsWith("@")) {
      if (!selected.find(s => s.address === q.toLowerCase()))
        setSelected(p => [...p, { address: q.toLowerCase(), name: toHandle(q.toLowerCase()), username: q.split("@")[0] }]);
      setPSearch(""); setShowDrop(false); return;
    }
    if (filtered.length > 0) { addUser(filtered[0]); return; }
    setSelected(p => [...p, { address: q, name: toHandle(q), username: q.replace(/^@/, "") }]);
    setPSearch(""); setShowDrop(false);
  };

  const aiAgenda = async () => {
    if (!title) return;
    setAiBusy(true);
    try {
      const { data } = await api.post("/meetings/ai/agenda", { topic: title, duration_min: duration, participants: selected.map(p => p.address) });
      setAgenda(data.agenda);
    } catch {}
    setAiBusy(false);
  };

  const create = async () => {
    if (!title || !when) return;
    setBusy(true);
    try {
      const { data } = await api.post("/meetings", {
        title, scheduled_at: new Date(when).toISOString(), duration_min: parseInt(duration, 10),
        host: user?.address || "you@hexawebos.com", participants: selected.map(p => p.address), agenda,
      });
      if (data.invited?.length) setInvitedNote(`Invitations delivered to ${data.invited.length} inbox(es).`);
      onCreated(data);
    } catch {}
    setBusy(false);
  };

  return (
    <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-20 p-6" onClick={onClose}>
      <div className="glass rounded-xl w-full max-w-2xl max-h-[90%] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-2 border-b border-white/10">
          <div className="text-sm font-medium">Schedule Meeting</div>
          <button onClick={onClose} className="btn-ghost p-1"><X size={12} /></button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1.5">Meeting type</div>
            <div className="grid grid-cols-3 gap-2">
              {MEETING_TYPES.map(t => (
                <button key={t.id} data-testid={`mtype-${t.id}`}
                  onClick={() => { setType(t.id); if (!title) setTitle(t.label); }}
                  className={`px-3 py-2 rounded text-left border ${type === t.id ? "border-orange-500 bg-orange-50" : "border-slate-200 hover:border-slate-300"}`}>
                  <div className="text-xs font-medium text-slate-800">{t.label}</div>
                  <div className="text-[10px] text-slate-500">{t.desc}</div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Title</div>
            <input data-testid="meeting-title" value={title} onChange={e => setTitle(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">When</div>
              <input data-testid="meeting-when" type="datetime-local" value={when} onChange={e => setWhen(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Duration (min)</div>
              <input data-testid="meeting-duration" type="number" min={5} max={480} value={duration} onChange={e => setDuration(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm" />
            </div>
          </div>

          {/* ─── Participant Picker ──────────────────────────────── */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1.5 flex items-center gap-1">
              <Users size={9} /> Participants
            </div>
            {selected.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {selected.map(p => (
                  <div key={p.address} data-testid={`participant-chip-${p.address}`}
                    className="flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-medium"
                    style={{ background: "rgba(255,122,46,0.15)", border: "1px solid rgba(255,122,46,0.35)", color: "#ff7a2e" }}>
                    <span className="w-4 h-4 rounded-full bg-[#ff7a2e]/25 flex items-center justify-center text-[9px] font-bold flex-shrink-0">
                      {(p.name || p.address)[0].toUpperCase()}
                    </span>
                    <span>{p.name && p.name !== p.address ? p.name : (p.username ? `@${p.username}` : toHandle(p.address))}</span>
                    <button onClick={() => removeUser(p.address)} className="hover:text-red-400 ml-0.5"><X size={9} /></button>
                  </div>
                ))}
              </div>
            )}
            <div className="relative" ref={pickerRef}>
              <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded px-3 py-2 focus-within:border-[#ff7a2e]/50">
                <Search size={12} className="text-white/30 flex-shrink-0" />
                <input
                  data-testid="meeting-participants-search"
                  value={pSearch}
                  onChange={e => { setPSearch(e.target.value); setShowDrop(true); }}
                  onFocus={() => setShowDrop(true)}
                  onKeyDown={onKeyDown}
                  placeholder="Type name, @username or email — Enter to add"
                  className="flex-1 bg-transparent text-sm outline-none placeholder-white/30"
                />
              </div>
              {showDrop && filtered.length > 0 && (
                <div className="absolute z-30 left-0 right-0 top-full mt-1 rounded-lg overflow-hidden shadow-xl bg-white border border-slate-200" style={{ maxHeight: 200 }}>
                  <div className="overflow-y-auto max-h-[200px]">
                    {filtered.map(u => (
                      <button key={u.address} data-testid={`dir-user-${u.address}`}
                        onClick={() => addUser(u)}
                        className="w-full flex items-center gap-3 px-3 py-2 hover:bg-slate-50 text-left transition-colors">
                        <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                          style={{ background: "linear-gradient(135deg,#ff7a2e,#8b5cf6)", color: "#fff" }}>
                          {(u.name || u.address)[0].toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-slate-900 truncate">{u.name || u.username}</div>
                          <div className="text-[10px] text-slate-500 truncate">{u.username ? `@${u.username}` : toHandle(u.address)}</div>
                        </div>
                        {u.role && <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{u.role}</span>}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="text-[10px] text-white/35 mt-1 flex items-center gap-1">
              <Sparkles size={9} color="#ff7a2e" /> Invitations delivered to each participant's HEXA inbox automatically.
            </div>
            {invitedNote && <div className="text-[10px] text-[#ff7a2e] mt-1">{invitedNote}</div>}
          </div>
          {/* ───────────────────────────────────────────────────── */}

          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] uppercase tracking-widest text-white/40">Agenda</div>
              <button data-testid="meeting-ai-agenda" disabled={aiBusy} onClick={aiAgenda} className="btn-ghost text-xs flex items-center gap-1">
                {aiBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} color="#ff7a2e" />} AI generate
              </button>
            </div>
            <textarea data-testid="meeting-agenda" value={agenda} onChange={e => setAgenda(e.target.value)} rows={6} className="w-full bg-white/5 border border-white/10 rounded p-2 text-xs mono" />
          </div>
        </div>
        <div className="px-4 py-3 border-t border-white/10 flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost text-xs">Cancel</button>
          <button data-testid="meeting-create" disabled={busy} onClick={create} className="btn-accent text-xs flex items-center gap-1">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Video size={12} />} Schedule & Invite
          </button>
        </div>
      </div>
    </div>
  );
}

function MeetingRoom({ meeting, onLeave }) {
  const { user } = useAuth();
  const [camOn, setCamOn] = useState(false);
  const [micOn, setMicOn] = useState(false);  // OFF by default — user explicitly enables
  const [localStream, setLocalStream] = useState(null);
  const [remotePeers, setRemotePeers] = useState({}); // peerId -> {name, stream}
  const [transcript, setTranscript] = useState("");
  const [minutes, setMinutes] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMsgs, setChatMsgs] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [unreadChat, setUnreadChat] = useState(0);
  const [chatToast, setChatToast] = useState(null); // { id, text } | null
  const [connected, setConnected] = useState(false);
  const [screenSharing, setScreenSharing] = useState(false);
  const [localScreenStream, setLocalScreenStream] = useState(null); // our own screen-share stream (separate from the camera)
  const [interimTranscript, setInterimTranscript] = useState("");
  const [transcriptLang, setTranscriptLang] = useState("en-US");
  const [transcribeError, setTranscribeError] = useState("");
  const [background, setBackground] = useState(BG_PRESETS[0]); // virtual background preset
  const [showBgPicker, setShowBgPicker] = useState(false);
  const [speakingPeers, setSpeakingPeers] = useState({}); // peerId -> true when speaking
  const audioCtxRef = useRef(null);

  // NEW: reactions, raise-hand, noise-cancellation, host controls
  const [reactions, setReactions] = useState([]);  // floating emoji {id, emoji, from, ts}
  const [showReactions, setShowReactions] = useState(false);
  const [handsRaised, setHandsRaised] = useState({}); // peerId -> true
  const [noiseSuppression, setNoiseSuppression] = useState(true);
  const [remoteMuted, setRemoteMuted] = useState({});  // peerId -> {audio:bool, video:bool} (for host-only view)

  // Local recording state — uses MediaRecorder to capture the composited local
  // preview + audio and downloads it as a WebM file when the user stops.
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);

  // Composite recording internals — an offscreen canvas that every participant's
  // video (camera + screen-share) is drawn onto each frame, plus an AudioContext
  // graph that mixes the local mic with every remote audio track into one track.
  // `recordingSourcesRef` holds the live set of { video, audioNode } entries keyed
  // by participant/stream id, kept in sync with who's actually in the call.
  const recordingCanvasRef = useRef(null);
  const recordingCtxRef = useRef(null);
  const recordingRafRef = useRef(null);
  const recordingAudioCtxRef = useRef(null);
  const recordingAudioDestRef = useRef(null);
  const recordingSourcesRef = useRef(new Map()); // key -> { stream, video, audioNode }

  // Refs (declared here so they're initialised BEFORE any derived expression /
  // useEffect below reads from them — moving these later would trip TDZ and
  // render the meeting room blank).
  const screenStreamRef = useRef(null);
  const screenTrackRef = useRef(null);
  const screenSharingRef = useRef(false);     // kept in sync with screenSharing, read by stopScreenShare/createPeerConnection
  const camOnBeforeShareRef = useRef(false);  // camOn snapshot taken right before a share starts
  const stopScreenShareFnRef = useRef(() => {}); // always points at the CURRENT stopScreenShare, for the track's 'ended' handler
  const localVideoRef = useRef(null);
  const recogRef = useRef(null);
  const wantListeningRef = useRef(false); // true while the user wants transcription running (survives onend restarts)
  const [transcriptOpen, setTranscriptOpen] = useState(true);
  const [minutesSentInfo, setMinutesSentInfo] = useState("");
  const wsRef = useRef(null);
  const chatOpenRef = useRef(false);      // kept in sync with chatOpen so the WS handler can read it without a stale closure
  const peerConnsRef = useRef({}); // peerId -> RTCPeerConnection
  const localStreamRef = useRef(null);
  const peerIdRef = useRef(`p-${Math.random().toString(36).slice(2, 10)}`);
  const myNameRef = useRef(user?.name || user?.username || "Guest");

  // ICE config — starts with STUN, extended with TURN if configured in Integrations
  const [ICE, setICE] = useState({ iceServers: [{ urls: "stun:stun.l.google.com:19302" }] });
  useEffect(() => {
    api.get("/meetings/ice-config")
      .then(r => { if (r.data?.iceServers?.length) setICE(r.data); })
      .catch(() => {}); // always fall back to STUN-only
  }, []);

  // Reset the unread badge the moment chat is opened; keep the ref current so
  // handleSignal's WS callback (set up once) always reads the latest value.
  useEffect(() => {
    chatOpenRef.current = chatOpen;
    if (chatOpen) setUnreadChat(0);
  }, [chatOpen]);

  // "You" are the host if your email matches the meeting creator/owner.
  const myEmail = (user?.address || user?.email || "").toLowerCase();
  const isHost = !!meeting && (
    (meeting.owner || "").toLowerCase() === myEmail ||
    (meeting.host_email || "").toLowerCase() === myEmail ||
    (meeting.created_by || "").toLowerCase() === myEmail
  );
  const iAmHandRaised = !!handsRaised[peerIdRef.current];

  // MediaPipe-powered virtual background — swaps out the video track on all
  // peer connections + local preview whenever the effect changes.
  const { outputStream: bgOutputStream } = useVirtualBackground(localStream, background);

  // The stream that should render in the local preview and be sent to peers:
  // either the composited stream (if a background effect is active + ready) or the raw camera.
  const displayStream = (background.type !== "none" && bgOutputStream) ? bgOutputStream : localStream;

  // Whenever we get a fresh composited stream, hot-swap it into every peer.
  useEffect(() => {
    if (!bgOutputStream) return;
    const vt = bgOutputStream.getVideoTracks()[0];
    if (vt) _replaceVideoTrackOnPeers(vt);
    // update the local preview element too
    if (localVideoRef.current) localVideoRef.current.srcObject = bgOutputStream;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bgOutputStream]);

  // When the user turns the effect OFF, restore the raw camera track.
  useEffect(() => {
    if (background.type === "none" && localStreamRef.current) {
      const raw = localStreamRef.current.getVideoTracks()[0];
      if (raw) _replaceVideoTrackOnPeers(raw);
      if (localVideoRef.current) localVideoRef.current.srcObject = localStreamRef.current;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [background.id]);

  // Set up WS signaling
  useEffect(() => {
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const wsScheme = backend.startsWith("https") ? "wss" : "ws";
    const host = backend.replace(/^https?:\/\//, "");
    const url = `${wsScheme}://${host}/api/ws/meetings/${meeting.room_id}?peer=${peerIdRef.current}&name=${encodeURIComponent(myNameRef.current)}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;
    ws.onopen = () => setConnected(true);
    ws.onclose = () => setConnected(false);
    ws.onmessage = (ev) => handleSignal(JSON.parse(ev.data));

    api.put(`/meetings/${meeting.id}`, { status: "live" });

    return () => {
      try { ws.close(); } catch {}
      Object.values(peerConnsRef.current).forEach(pc => { try { pc.close(); } catch {} });
      peerConnsRef.current = {};
      if (localStreamRef.current) localStreamRef.current.getTracks().forEach(t => t.stop());
      if (screenStreamRef.current) screenStreamRef.current.getTracks().forEach(t => t.stop());
      wantListeningRef.current = false;
      if (recogRef.current) try { recogRef.current.stop(); } catch {}
      if (mediaRecorderRef.current) { try { mediaRecorderRef.current.stop(); } catch {} }
      stopCompositeRecording();
      api.put(`/meetings/${meeting.id}`, { status: "ended" });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const wsSend = (msg) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    }
  };

  const createPeerConnection = (peerId, peerName, isInitiator) => {
    if (peerConnsRef.current[peerId]) return peerConnsRef.current[peerId];
    const pc = new RTCPeerConnection(ICE);
    peerConnsRef.current[peerId] = pc;
    pc._makingOffer = false;
    pc._ignoreOffer = false;
    pc._polite = peerIdRef.current > peerId; // deterministic polite peer selection

    // Attach existing local tracks. If we have none AND we are the initiator,
    // add recvonly transceivers so we can still receive remote media before
    // we turn our own camera on. Non-initiators skip pre-adding transceivers
    // and let setRemoteDescription create them from the incoming offer — this
    // avoids the "phantom" transceiver mismatch that leaves peer tiles blank.
    try {
      if (localStreamRef.current && localStreamRef.current.getTracks().length > 0) {
        localStreamRef.current.getTracks().forEach(t => pc.addTrack(t, localStreamRef.current));
      } else if (isInitiator) {
        pc.addTransceiver("video", { direction: "recvonly" });
        pc.addTransceiver("audio", { direction: "recvonly" });
      }
      // If we're already sharing our screen when this peer connects (e.g. they
      // joined mid-share), attach the screen as a SECOND video track so they
      // get it too, without touching the camera track above.
      if (screenSharingRef.current && screenStreamRef.current) {
        const st = screenStreamRef.current.getVideoTracks()[0];
        if (st) pc.addTrack(st, screenStreamRef.current);
      }
    } catch { /* noop */ }

    pc.onicecandidate = (e) => {
      if (e.candidate) wsSend({ type: "ice", to: peerId, candidate: e.candidate });
    };

    // Screen share arrives as a SECOND, independent video track/stream (see
    // startScreenShare) rather than replacing the camera track. We tell the
    // two apart by stream id: the first video stream we see for a peer is
    // their camera; any later video stream with a DIFFERENT id is a screen
    // share. Audio always rides along with the camera stream.
    pc.ontrack = (e) => {
      const track = e.track;
      const incoming = e.streams[0] || null;
      setRemotePeers(prev => {
        const existing = prev[peerId] || { name: peerName, stream: null, screenStream: null, camStreamId: null };
        let { stream, screenStream, camStreamId } = existing;

        if (track.kind === "audio") {
          if (!stream) stream = incoming || new MediaStream();
          if (!camStreamId && incoming) camStreamId = incoming.id;
          try { if (!stream.getTracks().includes(track)) stream.addTrack(track); } catch { /* noop */ }
        } else if (track.kind === "video") {
          const isSameAsCam = camStreamId && incoming && incoming.id === camStreamId;
          const isFirstVideo = !camStreamId;
          if (isFirstVideo || isSameAsCam) {
            if (!stream) stream = incoming || new MediaStream([track]);
            else if (incoming && !stream.getTracks().includes(track)) { try { stream.addTrack(track); } catch { /* noop */ } }
            camStreamId = incoming ? incoming.id : camStreamId;
          } else {
            // Different stream id from the known camera stream → screen share.
            screenStream = incoming || new MediaStream([track]);
          }
        }

        return { ...prev, [peerId]: { name: peerName || existing.name, stream, screenStream, camStreamId } };
      });
    };

    // Perfect-negotiation onnegotiationneeded: BOTH sides can renegotiate,
    // and we use the "polite peer" pattern in the offer handler to resolve glare.
    pc.onnegotiationneeded = async () => {
      try {
        pc._makingOffer = true;
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        wsSend({ type: "offer", to: peerId, sdp: pc.localDescription });
      } catch { /* noop */ }
      finally { pc._makingOffer = false; }
    };

    if (isInitiator) {
      pc.createOffer().then(o => pc.setLocalDescription(o)).then(() => {
        wsSend({ type: "offer", to: peerId, sdp: pc.localDescription });
      });
    }
    return pc;
  };

  const handleSignal = async (msg) => {
    const { type, from, name, peers } = msg;
    if (type === "peers") {
      // Existing peers already in the room. Add avatar tiles immediately so
      // participants are visible even before any media stream is negotiated.
      (peers || []).forEach((peer) => {
        const pid = typeof peer === "string" ? peer : peer?.id || peer?.peer;
        const pname = typeof peer === "string" ? "" : (peer?.name || "");
        if (!pid) return;
        setRemotePeers(prev => ({
          ...prev,
          [pid]: prev[pid] || { name: pname || "Peer", stream: null },
        }));
        createPeerConnection(pid, pname, true);
      });
    } else if (type === "peer-joined") {
      // A new participant just joined. Show them as an avatar tile until we
      // negotiate their media stream via SDP offer/answer.
      setRemotePeers(prev => ({ ...prev, [msg.peer]: prev[msg.peer] || { name: name || "Peer", stream: null } }));
    } else if (type === "peer-left") {
      const pc = peerConnsRef.current[msg.peer];
      if (pc) { try { pc.close(); } catch {} delete peerConnsRef.current[msg.peer]; }
      setRemotePeers(prev => { const n = { ...prev }; delete n[msg.peer]; return n; });
    } else if (type === "offer") {
      let pc = peerConnsRef.current[from];
      if (!pc) {
        pc = createPeerConnection(from, name, false);
      }
      // Perfect-negotiation glare handling: if we're currently making an offer
      // (or in a non-stable state) and we're the impolite peer, ignore this
      // incoming offer. The polite peer rolls back and accepts the remote offer.
      const offerCollision = pc._makingOffer || pc.signalingState !== "stable";
      pc._ignoreOffer = !pc._polite && offerCollision;
      if (pc._ignoreOffer) return;
      try {
        if (offerCollision) {
          await Promise.all([
            pc.setLocalDescription({ type: "rollback" }),
            pc.setRemoteDescription(new RTCSessionDescription(msg.sdp)),
          ]);
        } else {
          await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
        }
        const ans = await pc.createAnswer();
        await pc.setLocalDescription(ans);
        wsSend({ type: "answer", to: from, sdp: pc.localDescription });
      } catch { /* noop */ }
    } else if (type === "answer") {
      const pc = peerConnsRef.current[from];
      if (pc) await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
    } else if (type === "ice") {
      const pc = peerConnsRef.current[from];
      if (pc && msg.candidate) {
        try { await pc.addIceCandidate(new RTCIceCandidate(msg.candidate)); } catch {}
      }
    } else if (type === "chat") {
      const fromName = name || from;
      setChatMsgs(m => [...m, { from: fromName, text: msg.text, ts: msg.ts || Date.now() }]);
      if (!chatOpenRef.current) {
        setUnreadChat(c => c + 1);
        const tid = `ct-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        setChatToast({ id: tid, text: `${fromName}: ${msg.text}` });
        setTimeout(() => setChatToast(curr => (curr && curr.id === tid ? null : curr)), 6000);
      }
    } else if (type === "transcript") {
      if (msg.text) {
        const fromName = name || from;
        setTranscript(t => (t ? t + "\n" : "") + `${fromName}: ${msg.text}`);
      }
    } else if (type === "reaction") {
      const rid = `rx-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      setReactions(prev => [...prev, { id: rid, emoji: msg.emoji, from: name || from, ts: Date.now() }]);
      setTimeout(() => setReactions(prev => prev.filter(r => r.id !== rid)), 4200);
    } else if (type === "hand-raise") {
      setHandsRaised(prev => ({ ...prev, [msg.peer || from]: !!msg.raised }));
    } else if (type === "host-mute-audio") {
      // Server forwards this from the host; we obey by disabling our mic
      if (localStreamRef.current) {
        localStreamRef.current.getAudioTracks().forEach(t => { t.enabled = false; });
      }
      setMicOn(false);
      alert("The host has muted your microphone.");
    } else if (type === "host-mute-video") {
      if (localStreamRef.current) {
        localStreamRef.current.getVideoTracks().forEach(t => { try { t.stop(); } catch { /* noop */ } localStreamRef.current.removeTrack(t); });
      }
      setCamOn(false);
      await _replaceVideoTrackOnPeers(null);
      alert("The host has turned off your camera.");
    } else if (type === "screen-share" || type === "screen-stop") {
      // No-op: the presentation tile's presence is driven entirely by
      // remotePeers[pid].screenStream (set in pc.ontrack above) now that the
      // screen travels as its own track/stream instead of a label-only signal.
    }
  };

  const toggleCam = async () => {
    if (camOn && localStreamRef.current) {
      // Stop only VIDEO tracks — keep audio flowing.
      localStreamRef.current.getVideoTracks().forEach(t => { try { t.stop(); } catch { /* noop */ } localStreamRef.current.removeTrack(t); });
      setCamOn(false);
      // Send null video to all peers so their tile reverts to avatar.
      await _replaceVideoTrackOnPeers(null);
      return;
    }
    try {
      // HD constraints — 720p @ 30fps for crisp video without hammering CPU.
      const constraints = {
        video: {
          width:     { ideal: 1280, max: 1920 },
          height:    { ideal: 720,  max: 1080 },
          frameRate: { ideal: 30,   max: 30 },
          facingMode: "user",
        },
        audio: micOn ? {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl:  true,
        } : false,
      };
      const s = await navigator.mediaDevices.getUserMedia(constraints);
      const videoTrack = s.getVideoTracks()[0];
      const audioTrack = s.getAudioTracks()[0];

      // If we already have a localStreamRef (e.g. audio-only session), merge.
      if (localStreamRef.current) {
        if (videoTrack) localStreamRef.current.addTrack(videoTrack);
        // If we now have an extra audio track, prefer the newer one
        if (audioTrack) {
          localStreamRef.current.getAudioTracks().forEach(t => localStreamRef.current.removeTrack(t));
          localStreamRef.current.addTrack(audioTrack);
        }
      } else {
        localStreamRef.current = s;
      }
      setLocalStream(localStreamRef.current);
      setCamOn(true);
      if (localVideoRef.current) localVideoRef.current.srcObject = localStreamRef.current;

      // Ensure peers receive both audio and video via replaceTrack (no duplicate senders)
      if (videoTrack) await _replaceVideoTrackOnPeers(videoTrack);
      if (audioTrack) await _replaceAudioTrackOnPeers(audioTrack);
    } catch (e) {
      alert("Camera/microphone unavailable or denied. Please grant permission and try again.");
    }
  };

  /**
   * Same idea as _replaceVideoTrackOnPeers but for audio tracks.
   */
  const _replaceAudioTrackOnPeers = async (newTrack) => {
    for (const pc of Object.values(peerConnsRef.current)) {
      const senders = pc.getSenders();
      const audioSender = senders.find(s => s.track && s.track.kind === "audio");
      if (!audioSender && newTrack) {
        try { pc.addTrack(newTrack, localStreamRef.current || new MediaStream([newTrack])); } catch { /* noop */ }
      } else if (audioSender) {
        try { await audioSender.replaceTrack(newTrack); } catch { /* noop */ }
      }
    }
  };

  const toggleMic = async () => {
    const next = !micOn;
    setMicOn(next);
    if (localStreamRef.current) {
      const audioTracks = localStreamRef.current.getAudioTracks();
      if (audioTracks.length > 0) {
        audioTracks.forEach(t => { t.enabled = next; });
        return;
      }
    }
    // No audio track yet — user is enabling the mic for the first time.
    // Acquire it via getUserMedia and hot-swap onto every peer connection.
    if (next) {
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: noiseSuppression, autoGainControl: true },
          video: false,
        });
        const audioTrack = s.getAudioTracks()[0];
        if (!audioTrack) return;
        if (localStreamRef.current) {
          localStreamRef.current.addTrack(audioTrack);
        } else {
          localStreamRef.current = s;
          setLocalStream(s);
        }
        await _replaceAudioTrackOnPeers(audioTrack);
      } catch (e) {
        setMicOn(false);
        alert("Microphone unavailable or denied.");
      }
    }
  };

  /**
   * Replace the outgoing video track on every peer connection with `newTrack`.
   * Uses sender.replaceTrack() so peers get the new stream without a full re-offer.
   * Pass `null` to remove video entirely.
   */
  const _replaceVideoTrackOnPeers = async (newTrack) => {
    for (const pc of Object.values(peerConnsRef.current)) {
      const senders = pc.getSenders();
      // Match by kind on the sender's transceiver (works even when there is no track yet)
      let videoSender = senders.find(s => (s.track && s.track.kind === "video"));
      let tv = null;
      if (!videoSender) {
        // Fallback: the video transceiver created while our camera was off
        // (recvonly on the joiner, recvonly/inactive on the answerer).
        tv = pc.getTransceivers().find(x => x.receiver?.track?.kind === "video" && !x.stopped);
        videoSender = tv?.sender;
      }
      if (!videoSender && newTrack) {
        try { pc.addTrack(newTrack, localStreamRef.current || new MediaStream([newTrack])); } catch { /* noop */ }
      } else if (videoSender) {
        try { await videoSender.replaceTrack(newTrack); } catch { /* noop */ }
        // replaceTrack alone never sends on a receive-only transceiver — that
        // is why remote tiles stayed on the avatar. Flip it to sendrecv (this
        // fires onnegotiationneeded, which re-offers) and tie it to our stream.
        if (tv && newTrack && !String(tv.direction).startsWith("send")) {
          try { videoSender.setStreams?.(localStreamRef.current || new MediaStream([newTrack])); } catch { /* noop */ }
          tv.direction = "sendrecv";
        }
      }
    }
  };

  const startScreenShare = async () => {
    if (screenSharingRef.current) return;
    if (!navigator.mediaDevices?.getDisplayMedia) {
      alert("Screen sharing is not supported in this browser.");
      return;
    }
    let display;
    try {
      display = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: 15 },
        audio: false,
      });
    } catch {
      // User cancelled the picker or permission denied
      return;
    }
    const screenTrack = display.getVideoTracks()[0];
    if (!screenTrack) return;

    camOnBeforeShareRef.current = camOn;
    screenStreamRef.current = display;
    screenTrackRef.current = screenTrack;
    screenSharingRef.current = true;
    setScreenSharing(true);
    setLocalScreenStream(display);

    // Send the screen as a SECOND video track on every peer connection —
    // the camera track (if any) keeps flowing untouched on its own sender,
    // so viewers see both a presentation tile and a small camera tile.
    for (const pc of Object.values(peerConnsRef.current)) {
      try { pc.addTrack(screenTrack, display); } catch { /* noop */ }
    }

    // Broadcast share status so other peers can label the tile
    wsSend({ type: "screen-share", peer: peerIdRef.current });

    // Auto-stop when the user clicks the browser's native "Stop sharing" UI.
    // Routed through a ref so this always calls the CURRENT stopScreenShare,
    // never a stale closure captured at share-start time.
    screenTrack.addEventListener("ended", () => { stopScreenShareFnRef.current?.(); });
  };

  const stopScreenShare = async () => {
    if (!screenSharingRef.current) return;
    screenSharingRef.current = false;

    const display = screenStreamRef.current;
    const screenTrack = screenTrackRef.current;

    // Remove the screen sender from every peer BEFORE stopping the track, so
    // a stopped track never lingers on a live sender (that's what caused the
    // remote-side blackout: the sender kept referencing a dead track).
    for (const pc of Object.values(peerConnsRef.current)) {
      const sender = pc.getSenders().find(s => s.track === screenTrack);
      if (sender) { try { pc.removeTrack(sender); } catch { /* noop */ } }
    }
    if (display) display.getTracks().forEach(t => { try { t.stop(); } catch { /* noop */ } });
    screenStreamRef.current = null;
    screenTrackRef.current = null;
    setScreenSharing(false);
    setLocalScreenStream(null);

    // Notify peers we stopped sharing
    wsSend({ type: "screen-stop", peer: peerIdRef.current });

    // The camera track was never replaced by the share (it's an independent
    // second track), so there's nothing to re-acquire — just restore the
    // on/off UI state to whatever it was right before sharing started. If the
    // camera was off before, leave it off instead of leaving a stray track.
    const wasCamOn = camOnBeforeShareRef.current;
    setCamOn(wasCamOn);
    if (!wasCamOn && localStreamRef.current && localStreamRef.current.getVideoTracks().length > 0) {
      localStreamRef.current.getVideoTracks().forEach(t => { try { t.stop(); } catch { /* noop */ } localStreamRef.current.removeTrack(t); });
      await _replaceVideoTrackOnPeers(null);
    }
    if (localVideoRef.current) localVideoRef.current.srcObject = localStreamRef.current;
    // If a background effect is active, useVirtualBackground keys its effect
    // off the camera track's id, so restoring the camera above re-triggers it
    // on its own — no manual "restart" call needed here.
  };

  const toggleScreenShare = () => {
    if (screenSharing) stopScreenShare(); else startScreenShare();
  };

  // Keep this pointed at the latest stopScreenShare on every render so the
  // screen track's native 'ended' listener (added once, at share-start) never
  // invokes a stale closure.
  useEffect(() => { stopScreenShareFnRef.current = stopScreenShare; });

  const SR_SUPPORTED = typeof window !== "undefined" && !!(window.SpeechRecognition || window.webkitSpeechRecognition);

  const startRecognition = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const r = new SR();
    r.continuous = true;
    r.interimResults = true;
    r.lang = transcriptLang;
    r.onstart = () => { setListening(true); setTranscribeError(""); };
    r.onend = () => {
      setListening(false);
      setInterimTranscript("");
      // Some browsers silently end recognition after a pause of silence —
      // restart automatically while the user hasn't explicitly stopped it.
      if (wantListeningRef.current) {
        try { startRecognition(); } catch { /* noop */ }
      }
    };
    r.onerror = (ev) => {
      const reason = ev?.error;
      if (reason === "not-allowed" || reason === "service-not-allowed" || reason === "audio-capture") wantListeningRef.current = false;
      if (reason === "not-allowed") setTranscribeError("Microphone access denied — allow it to transcribe.");
      else if (reason === "no-speech") setTranscribeError("No speech detected.");
      else if (reason === "network") setTranscribeError("Network error — speech recognition needs an internet connection.");
      else if (reason) setTranscribeError(`Speech recognition error: ${reason}`);
    };
    r.onresult = (ev) => {
      let finalText = "";
      let interim = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const chunk = ev.results[i][0].transcript;
        if (ev.results[i].isFinal) finalText += chunk + " ";
        else interim += chunk;
      }
      setInterimTranscript(interim);
      if (finalText.trim()) {
        const text = finalText.trim();
        setTranscript(t => (t ? t + "\n" : "") + `${myNameRef.current}: ${text}`);
        wsSend({ type: "transcript", text, name: myNameRef.current });
      }
    };
    recogRef.current = r;
    try { r.start(); } catch { /* noop — e.g. already started */ }
  };

  const toggleTranscribe = () => {
    if (!SR_SUPPORTED) { setTranscribeError("Chrome or Edge required for live transcription."); return; }
    if (listening) {
      wantListeningRef.current = false;
      try { recogRef.current?.stop(); } catch { /* noop */ }
      setInterimTranscript("");
      return;
    }
    wantListeningRef.current = true;
    startRecognition();
  };

  // Support #301778: live transcript starts automatically when joining (Chrome/Edge).
  useEffect(() => {
    if (!SR_SUPPORTED) return undefined;
    const t = setTimeout(() => {
      if (!wantListeningRef.current) { wantListeningRef.current = true; startRecognition(); }
    }, 1500);
    return () => {
      clearTimeout(t);
      wantListeningRef.current = false;
      try { recogRef.current?.stop(); } catch { /* noop */ }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const genMinutes = async () => {
    if (!transcript.trim()) return;
    setAiBusy(true);
    setMinutesSentInfo("");
    try {
      const { data } = await api.post("/meetings/ai/minutes", { transcript });
      setMinutes(data.minutes);
      await api.put(`/meetings/${meeting.id}`, { transcript, ai_summary: data.minutes });
      try {
        const { data: sent } = await api.post(`/meetings/${meeting.id}/minutes/send`, { minutes: data.minutes, transcript });
        const n = (sent?.sent_to || []).length;
        setMinutesSentInfo(`Emailed to ${n} participant${n === 1 ? "" : "s"}`);
      } catch { setMinutesSentInfo("Minutes saved, but emailing them failed."); }
    } catch { setMinutes("AI unavailable."); }
    setAiBusy(false);
  };

  const sendChat = () => {
    if (!chatInput.trim()) return;
    const msg = { from: peerIdRef.current, name: myNameRef.current, text: chatInput, ts: Date.now() };
    setChatMsgs(m => [...m, { from: "You", text: chatInput, ts: msg.ts }]);
    wsSend({ type: "chat", text: chatInput });
    setChatInput("");
  };

  /* Reactions — broadcast an emoji that floats up for ~4 seconds on every screen. */
  const sendReaction = (emoji) => {
    const rid = `rx-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setReactions(prev => [...prev, { id: rid, emoji, from: "You", ts: Date.now() }]);
    setTimeout(() => setReactions(prev => prev.filter(r => r.id !== rid)), 4200);
    wsSend({ type: "reaction", emoji });
    setShowReactions(false);
  };

  /* Raise / lower hand — persists until I lower it. */
  const toggleHand = () => {
    const raised = !iAmHandRaised;
    setHandsRaised(prev => ({ ...prev, [peerIdRef.current]: raised }));
    wsSend({ type: "hand-raise", raised });
  };

  /* Host-only: mute another participant's microphone or camera. */
  const hostMute = (peerId, kind /* "audio" | "video" */) => {
    if (!isHost) return;
    wsSend({ type: kind === "audio" ? "host-mute-audio" : "host-mute-video", to: peerId });
    setRemoteMuted(prev => ({ ...prev, [peerId]: { ...(prev[peerId] || {}), [kind]: true } }));
  };
  const hostMuteAll = () => {
    if (!isHost) return;
    Object.keys(remotePeers).forEach(pid => hostMute(pid, "audio"));
  };

  /* Toggle browser-level noise suppression on the fly by applying constraints. */
  const toggleNoiseSuppression = async () => {
    const next = !noiseSuppression;
    setNoiseSuppression(next);
    const audioTrack = localStreamRef.current?.getAudioTracks?.()[0];
    if (audioTrack && typeof audioTrack.applyConstraints === "function") {
      try {
        await audioTrack.applyConstraints({
          noiseSuppression: next,
          echoCancellation: next,
          autoGainControl:  next,
        });
      } catch { /* noop */ }
    }
  };

  /* ---- Composite recording helpers ----
     A source is one participant's camera OR screen-share stream. Each gets an
     offscreen (never-attached-to-DOM) <video> element to draw frames from, and —
     if it carries audio — a node wired into the shared AudioContext mix. */
  const addRecordingSource = (key, stream) => {
    if (!stream) return;
    const sources = recordingSourcesRef.current;
    const existing = sources.get(key);
    if (existing?.stream === stream) return;
    if (existing) removeRecordingSource(key);

    const entry = { stream, video: null, audioNode: null };
    if (stream.getVideoTracks().length) {
      const video = document.createElement("video");
      video.muted = true;
      video.playsInline = true;
      // Chromium only runs the WebRTC video-decode pipeline for a <video> that's
      // attached to the document — a fully detached element never advances past
      // readyState 0 for a REMOTE track (local camera tracks decode fine detached,
      // since they skip that pipeline, which is why this only showed up with a
      // second real participant in the call). Keep it off-screen but attached.
      video.style.cssText = "position:fixed;width:1px;height:1px;opacity:0;pointer-events:none;top:-9999px;left:-9999px;";
      document.body.appendChild(video);
      video.srcObject = stream;
      video.play().catch(() => { /* noop */ });
      entry.video = video;
    }
    const audioCtx = recordingAudioCtxRef.current;
    const dest = recordingAudioDestRef.current;
    if (audioCtx && dest && stream.getAudioTracks().length) {
      try {
        const node = audioCtx.createMediaStreamSource(stream);
        node.connect(dest);
        entry.audioNode = node;
      } catch { /* noop — e.g. a track the browser won't let us tap twice */ }
    }
    sources.set(key, entry);
  };

  const removeRecordingSource = (key) => {
    const sources = recordingSourcesRef.current;
    const entry = sources.get(key);
    if (!entry) return;
    if (entry.video) { try { entry.video.pause(); entry.video.srcObject = null; entry.video.remove(); } catch { /* noop */ } }
    if (entry.audioNode) { try { entry.audioNode.disconnect(); } catch { /* noop */ } }
    sources.delete(key);
  };

  /* Reconciles the recording source map against who's actually in the call right
     now — local cam, local screen-share, and every remote peer's cam/screen. Runs
     whenever the roster changes (see the effect below) so people joining or
     leaving mid-recording show up/disappear in the composite without a restart. */
  const syncRecordingSources = () => {
    const wanted = new Map();

    const myVideoTrack = displayStream?.getVideoTracks?.()[0] || localStream?.getVideoTracks?.()[0];
    const myAudioTrack = localStream?.getAudioTracks?.()[0];
    const myTracks = [myVideoTrack, myAudioTrack].filter(Boolean);
    if (myTracks.length) wanted.set("you", new MediaStream(myTracks));

    if (screenSharing && localScreenStream) wanted.set("you-screen", localScreenStream);

    Object.entries(remotePeers).forEach(([pid, p]) => {
      if (p.stream) wanted.set(pid, p.stream);
      if (p.screenStream) wanted.set(`${pid}-screen`, p.screenStream);
    });

    wanted.forEach((stream, key) => addRecordingSource(key, stream));
    Array.from(recordingSourcesRef.current.keys()).forEach((key) => {
      if (!wanted.has(key)) removeRecordingSource(key);
    });
  };

  // Keep the composite's sources in lockstep with the live roster while recording.
  useEffect(() => {
    if (!isRecording) return;
    syncRecordingSources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecording, remotePeers, screenSharing, localScreenStream, localStream, displayStream]);

  /* Draws every active source onto the offscreen canvas each frame: a shared
     screen (if any) fills the frame with camera thumbnails along the bottom,
     otherwise every camera gets an equal tile in a grid — same layout logic as
     the on-screen gallery, just rasterized. */
  const drawRecordingFrame = () => {
    const canvas = recordingCanvasRef.current;
    const ctx = recordingCtxRef.current;
    if (!canvas || !ctx) return;
    const { width, height } = canvas;

    ctx.fillStyle = "#0b0f1a";
    ctx.fillRect(0, 0, width, height);

    const drawCover = (video, x, y, w, h) => {
      if (!video || !video.videoWidth || !video.videoHeight) return;
      const scale = Math.max(w / video.videoWidth, h / video.videoHeight);
      const vw = video.videoWidth * scale, vh = video.videoHeight * scale;
      ctx.save();
      ctx.beginPath();
      ctx.rect(x, y, w, h);
      ctx.clip();
      ctx.drawImage(video, x + (w - vw) / 2, y + (h - vh) / 2, vw, vh);
      ctx.restore();
    };

    const screenEntries = [];
    const camEntries = [];
    recordingSourcesRef.current.forEach((entry, key) => {
      if (!entry.video) return;
      (key.endsWith("-screen") ? screenEntries : camEntries).push(entry);
    });

    if (screenEntries.length) {
      drawCover(screenEntries[0].video, 0, 0, width, height);
      const thumbW = width / 6, thumbH = thumbW * 0.75, pad = 12;
      camEntries.slice(0, 6).forEach((entry, i) => {
        drawCover(entry.video, width - pad - (i + 1) * (thumbW + pad), height - thumbH - pad, thumbW, thumbH);
      });
    } else if (camEntries.length) {
      const cols = Math.ceil(Math.sqrt(camEntries.length));
      const rows = Math.ceil(camEntries.length / cols);
      const cellW = width / cols, cellH = height / rows;
      camEntries.forEach((entry, i) => {
        drawCover(entry.video, (i % cols) * cellW, Math.floor(i / cols) * cellH, cellW, cellH);
      });
    }

    recordingRafRef.current = requestAnimationFrame(drawRecordingFrame);
  };

  /* Tears down the canvas draw loop, every offscreen video element / audio node,
     and the AudioContext itself — called both on Stop and if recording start fails. */
  const stopCompositeRecording = () => {
    if (recordingRafRef.current) cancelAnimationFrame(recordingRafRef.current);
    recordingRafRef.current = null;
    Array.from(recordingSourcesRef.current.keys()).forEach(removeRecordingSource);
    if (recordingAudioCtxRef.current) {
      try { recordingAudioCtxRef.current.close(); } catch { /* noop */ }
    }
    recordingAudioCtxRef.current = null;
    recordingAudioDestRef.current = null;
    recordingCanvasRef.current = null;
    recordingCtxRef.current = null;
  };

  /* Composite recording — draws every participant's camera + any shared screen
     onto an offscreen canvas and mixes every participant's audio (local mic +
     each remote peer, including screen-share audio) into one track, so the
     downloaded file reflects the whole call, not just the local preview.
     NOTE: this only captures what THIS browser is receiving — it's a client-side
     recording, not a server-side/cloud one, so it only includes whoever is in
     the call from this user's point of view at record time. */
  const toggleRecording = async () => {
    if (isRecording) {
      try { mediaRecorderRef.current?.stop(); } catch { /* noop */ }
      setIsRecording(false);
      stopCompositeRecording();
      return;
    }
    if (!localStreamRef.current) {
      alert("Turn on your camera or microphone before recording.");
      return;
    }
    try {
      recordedChunksRef.current = [];

      const canvas = document.createElement("canvas");
      canvas.width = 1280;
      canvas.height = 720;
      recordingCanvasRef.current = canvas;
      recordingCtxRef.current = canvas.getContext("2d");

      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const audioCtx = new AudioCtx();
      const dest = audioCtx.createMediaStreamDestination();
      recordingAudioCtxRef.current = audioCtx;
      recordingAudioDestRef.current = dest;

      syncRecordingSources();
      drawRecordingFrame();

      const canvasStream = canvas.captureStream(30);
      const combined = new MediaStream([
        ...canvasStream.getVideoTracks(),
        ...dest.stream.getAudioTracks(),
      ]);

      const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
        ? "video/webm;codecs=vp9,opus"
        : (MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus") ? "video/webm;codecs=vp8,opus" : "video/webm");
      const rec = new MediaRecorder(combined, { mimeType: mime, videoBitsPerSecond: 2_500_000 });
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) recordedChunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: mime });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const ts = new Date().toISOString().replace(/[:.]/g, "-");
        a.download = `hexa-meeting-${meeting.title || "recording"}-${ts}.webm`;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
      };
      rec.start(1000); // flush chunks every second so nothing is lost on crash
      mediaRecorderRef.current = rec;
      setIsRecording(true);
    } catch (e) {
      stopCompositeRecording();
      alert("Recording failed: " + (e?.message || "unknown error"));
    }
  };

  // Camera tiles — one per participant (local + remote), always rendered small
  // once anyone is presenting (see screenTiles below).
  const tiles = [
    { key: "you", name: toHandle(myNameRef.current), stream: displayStream, color: "#ff7a2e", testId: "meeting-tile-you", isLocal: true },
    ...Object.entries(remotePeers).map(([pid, p], i) => ({
      key: pid, name: toHandle(p.name), stream: p.stream, color: ["#3b82f6","#10b981","#a855f7","#eab308"][i % 4], testId: `meeting-tile-${pid}`,
      isSpeaking: !!speakingPeers[pid],
    })),
  ];

  // Presentation tiles — the shared-screen stream(s), rendered large. A
  // screen share now travels as a SECOND video track, so it's tracked
  // entirely separately from the camera tile above instead of replacing it.
  const screenTiles = [
    ...(screenSharing && localScreenStream
      ? [{ key: "you-screen", name: `${toHandle(myNameRef.current)} (you)`, stream: localScreenStream, color: "#8b5cf6", testId: "meeting-tile-you-screen" }]
      : []),
    ...Object.entries(remotePeers)
      .filter(([, p]) => !!p.screenStream)
      .map(([pid, p]) => ({ key: `${pid}-screen`, name: `${toHandle(p.name)} (screen)`, stream: p.screenStream, color: "#8b5cf6", testId: `meeting-tile-${pid}-screen` })),
  ];

  // Gallery grid: up to 4 tiles per row (Zoom-style). Tile count determines cols.
  // On mobile (< md), stack in 1-2 cols to keep tiles legible.
  const gridCols = (() => {
    const n = tiles.length;
    if (n <= 1) return "grid-cols-1";
    if (n === 2) return "grid-cols-1 sm:grid-cols-2";
    if (n === 3) return "grid-cols-2 sm:grid-cols-3";
    return "grid-cols-2 md:grid-cols-4"; // 4+ tiles: 2/row on phone, 4/row on desktop
  })();

  return (
    <div className="flex-1 flex flex-col bg-black min-h-0 overflow-hidden relative">
      {/* Floating reactions overlay — full-screen so emoji float up over the tiles */}
      <div className="pointer-events-none absolute inset-0 z-30 overflow-hidden" data-testid="meeting-reactions-overlay">
        {reactions.map(r => (
          <div
            key={r.id}
            className="absolute text-5xl select-none"
            style={{
              left: `${20 + (parseInt(r.id.slice(-4), 36) % 60)}%`,
              bottom: 60,
              animation: "hexa-rx-float 4s ease-out forwards",
            }}
          >
            {r.emoji}
            <div className="text-[9px] mono text-white/60 text-center mt-0.5 uppercase tracking-widest">{r.from}</div>
          </div>
        ))}
      </div>

      {/* Chat toast — shows briefly when a message arrives while chat is closed */}
      {chatToast && (
        <button
          data-testid="meeting-chat-toast"
          onClick={() => { setChatOpen(true); setChatToast(null); }}
          className="absolute bottom-20 right-4 z-40 max-w-[260px] bg-white border-2 border-red-500 rounded-lg px-3 py-2 text-[11px] text-slate-800 shadow-2xl text-left cursor-pointer"
        >
          {chatToast.text}
        </button>
      )}

      {/* Persistent hand-raise banners — one per participant, stacked bottom-right */}
      <div className="pointer-events-none absolute right-84 bottom-16 z-30 flex flex-col items-end gap-2" data-testid="meeting-hands-overlay" style={{ right: "22rem" }}>
        {Object.entries(handsRaised).filter(([, raised]) => raised).map(([pid]) => {
          const t = tiles.find(x => x.key === pid);
          if (!t) return null;
          return (
            <div
              key={pid}
              data-testid={`meeting-hand-banner-${pid}`}
              className="flex items-center gap-2 bg-yellow-400/90 text-slate-900 rounded-full pl-2 pr-3 py-1 shadow-lg"
              style={{ animation: "hexa-hand-in 260ms cubic-bezier(0.16,1,0.3,1)" }}
            >
              <span className="text-lg" style={{ animation: "hexa-hand-wave 1.6s ease-in-out infinite" }}>✋</span>
              <div className="text-[11px] font-semibold uppercase tracking-wider">{t.name}{t.isLocal && " · You"}</div>
            </div>
          );
        })}
      </div>

      <style>{`
        @keyframes hexa-rx-float {
          0%   { transform: translateY(0) scale(0.6); opacity: 0; }
          15%  { transform: translateY(-40px) scale(1.15); opacity: 1; }
          70%  { transform: translateY(-260px) scale(1); opacity: 1; }
          100% { transform: translateY(-360px) scale(0.9); opacity: 0; }
        }
        @keyframes hexa-hand-in {
          0% { transform: translateX(40px); opacity: 0; }
          100% { transform: translateX(0); opacity: 1; }
        }
        @keyframes hexa-hand-wave {
          0%, 100% { transform: rotate(-8deg); }
          50%      { transform: rotate(12deg); }
        }
      `}</style>
      <div className="px-4 py-2 border-b border-white/10 flex items-center justify-between shrink-0">
        <div>
          <div className="text-sm font-medium">{meeting.title}</div>
          <div className="text-[10px] text-white/40 mono flex items-center gap-2">
            <span className={`w-1.5 h-1.5 rounded-full ${connected ? "bg-emerald-400" : "bg-red-400"}`} />
            Room {meeting.room_id} · {connected ? "Connected" : "Connecting…"} · {Object.keys(remotePeers).length} peer(s)
          </div>
        </div>
        <button onClick={onLeave} data-testid="meeting-leave" className="bg-red-500 hover:bg-red-600 text-white text-xs px-3 py-1.5 rounded flex items-center gap-1">
          <PhoneOff size={12} /> Leave
        </button>
      </div>

      <div className="flex-1 flex flex-col md:flex-row min-h-0">
        <div className="flex-1 p-2 sm:p-4 flex flex-col min-h-0 min-w-0">
          {screenSharing && (
            <div
              data-testid="meeting-sharing-banner"
              className="mb-2 px-3 py-1.5 border border-[#8b5cf6]/40 bg-[#8b5cf6]/12 text-[#a78bfa] mono text-[10px] uppercase tracking-widest flex items-center justify-between"
            >
              <span className="flex items-center gap-2">
                <ScreenShare size={12} /> you are sharing your screen with the meeting
              </span>
              <button onClick={stopScreenShare} className="text-[#3b82f6] hover:text-[#f87171] underline-offset-2 hover:underline">stop sharing</button>
            </div>
          )}
          {screenTiles.length > 0 ? (
            /* Presentation mode — shared screen(s) large, cameras as a small thumbnail strip */
            <div className="flex-1 min-h-0 flex flex-col gap-3">
              <div className={`flex-1 min-h-0 grid gap-3 ${screenTiles.length > 1 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"}`}>
                {screenTiles.map((t) => (
                  <Tile key={t.key} name={t.name} color={t.color} stream={t.stream} testId={t.testId} sharing />
                ))}
              </div>
              <div className="flex gap-2 overflow-x-auto shrink-0 pb-0.5" style={{ height: 110 }}>
                {tiles.map((t) => (
                  <div key={t.key} className="shrink-0" style={{ width: 160, height: 100 }}>
                    <Tile
                      name={t.name}
                      color={t.color}
                      stream={t.stream}
                      testId={t.testId}
                      videoRef={t.isLocal ? localVideoRef : undefined}
                      muted={t.isLocal}
                      isLocal={t.isLocal}
                      isSpeaking={t.isSpeaking}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* Gallery grid — up to 4 tiles per row */
            <div className={`flex-1 min-h-0 grid ${gridCols} gap-3 overflow-auto auto-rows-fr`}>
              {tiles.map((t) => (
                <Tile
                  key={t.key}
                  name={t.name}
                  color={t.color}
                  stream={t.stream}
                  testId={t.testId}
                  videoRef={t.isLocal ? localVideoRef : undefined}
                  muted={t.isLocal}
                  isLocal={t.isLocal}
                  isSpeaking={t.isSpeaking}
                />
              ))}
            </div>
          )}

          <div className="mt-3 flex items-center justify-center gap-2 shrink-0 relative">
            <button onClick={toggleCam} data-testid="meeting-cam" className={`w-11 h-11 rounded-full flex items-center justify-center border border-white/10 ${camOn ? "bg-white/10" : "bg-red-500/20"}`}>
              {camOn ? <Video size={16} /> : <VideoOff size={16} className="text-red-300" />}
            </button>
            <button onClick={toggleMic} data-testid="meeting-mic" className={`w-11 h-11 rounded-full flex items-center justify-center border border-white/10 ${micOn ? "bg-white/10" : "bg-red-500/20"}`}>
              {micOn ? <Mic size={16} /> : <MicOff size={16} className="text-red-300" />}
            </button>
            <button onClick={toggleTranscribe} data-testid="meeting-transcribe" className={`w-11 h-11 rounded-full flex items-center justify-center border border-white/10 ${listening ? "bg-[#ff7a2e] pulse-amber" : "bg-white/10"}`} title="Live transcribe">
              <Sparkles size={16} />
            </button>
            <button onClick={() => setChatOpen(!chatOpen)} data-testid="meeting-chat-toggle" className={`relative w-11 h-11 rounded-full flex items-center justify-center border-2 ${chatOpen ? "bg-[#3b82f6] border-[#3b82f6] text-white" : `bg-white/10 ${unreadChat > 0 ? "border-red-400" : "border-white/20"}`}`}>
              <MessageSquare size={16} />
              {unreadChat > 0 && (
                <span data-testid="meeting-chat-unread" className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white animate-pulse">
                  {unreadChat > 9 ? "9+" : unreadChat}
                </span>
              )}
            </button>
            <button
              onClick={toggleScreenShare}
              data-testid="meeting-screenshare"
              className={`w-11 h-11 rounded-full flex items-center justify-center border border-white/10 ${screenSharing ? "bg-[#8b5cf6]/25 text-[#8b5cf6] pulse-amber" : "bg-white/10"}`}
              title={screenSharing ? "Stop sharing screen" : "Share your screen"}
            >
              <ScreenShare size={16} />
            </button>

            {/* Raise-hand */}
            <button
              onClick={toggleHand}
              data-testid="meeting-raise-hand"
              className={`w-11 h-11 rounded-full flex items-center justify-center border border-white/10 ${iAmHandRaised ? "bg-yellow-400/25 text-yellow-300" : "bg-white/10"}`}
              title={iAmHandRaised ? "Lower hand" : "Raise hand"}
            >
              <Hand size={16} />
            </button>

            {/* Reactions */}
            <button
              onClick={() => setShowReactions(v => !v)}
              data-testid="meeting-reactions"
              className={`w-11 h-11 rounded-full flex items-center justify-center border border-white/10 ${showReactions ? "bg-pink-500/25 text-pink-300" : "bg-white/10"}`}
              title="Send a reaction"
            >
              <Smile size={16} />
            </button>
            {showReactions && (
              <div
                data-testid="meeting-reactions-picker"
                onMouseLeave={() => setShowReactions(false)}
                className="absolute bottom-14 left-1/2 -translate-x-1/2 z-40 bg-[#0b1220]/95 backdrop-blur-xl border border-white/15 rounded-full px-3 py-2 flex items-center gap-1.5 shadow-2xl"
              >
                {["👏", "👍", "❤️", "😂", "🎉", "🙌", "🔥", "💯"].map(e => (
                  <button
                    key={e}
                    onClick={() => sendReaction(e)}
                    data-testid={`meeting-reaction-${e}`}
                    className="text-2xl w-9 h-9 rounded-full hover:bg-white/10 transition"
                  >
                    {e}
                  </button>
                ))}
              </div>
            )}

            {/* Noise suppression toggle */}
            <button
              onClick={toggleNoiseSuppression}
              data-testid="meeting-noise"
              className={`w-11 h-11 rounded-full flex items-center justify-center border border-white/10 ${noiseSuppression ? "bg-emerald-500/25 text-emerald-300" : "bg-white/10"}`}
              title={noiseSuppression ? "Noise cancellation ON" : "Noise cancellation OFF"}
            >
              {noiseSuppression ? <Volume2 size={16} /> : <VolumeX size={16} />}
            </button>

            {/* Recording */}
            <button
              onClick={toggleRecording}
              data-testid="meeting-record"
              className={`w-11 h-11 rounded-full flex items-center justify-center border border-white/10 ${isRecording ? "bg-red-500/40 text-red-200 animate-pulse" : "bg-white/10"}`}
              title={isRecording ? "Stop recording" : "Record this meeting"}
            >
              {isRecording ? <Square size={14} /> : <Circle size={14} fill="currentColor" />}
            </button>
            <button
              onClick={() => setShowBgPicker(v => !v)}
              data-testid="meeting-effects"
              className={`w-11 h-11 rounded-full flex items-center justify-center border border-white/10 ${background.id !== "none" ? "bg-[#3b82f6]/25 text-[#3b82f6]" : "bg-white/10"}`}
              title="Change background / Effects"
            >
              <Wand2 size={16} />
            </button>

            {/* Background / Effects picker — Google Meet-style categorized gallery */}
            {showBgPicker && (
              <div
                data-testid="meeting-bg-picker"
                className="absolute bottom-14 right-0 z-40 bg-[#0b1220]/95 backdrop-blur-xl border border-white/15 rounded-xl p-3 w-[520px] shadow-2xl"
                onMouseLeave={() => setShowBgPicker(false)}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[13px] font-semibold text-white/90 flex items-center gap-1.5">
                    <ImageIcon size={13} /> Backgrounds and effects
                  </div>
                  <button onClick={() => setShowBgPicker(false)} className="text-white/40 hover:text-white/80 p-1"><X size={12} /></button>
                </div>

                {/* Effects row: None + Blur variants */}
                <div className="flex items-center gap-2 mb-3">
                  {BG_PRESETS.filter(p => p.category === "Effects").map(p => {
                    const on = background.id === p.id;
                    return (
                      <button
                        key={p.id}
                        onClick={() => { setBackground(p); }}
                        data-testid={`meeting-bg-${p.id}`}
                        className={`relative rounded-full border-2 transition flex items-center justify-center overflow-hidden ${on ? "border-[#3b82f6] ring-2 ring-[#3b82f6]/40" : "border-white/10 hover:border-white/30"}`}
                        style={{ width: 56, height: 56 }}
                        title={p.label}
                      >
                        {p.type === "blur" ? (
                          <div className="w-full h-full bg-gradient-to-br from-slate-500 to-slate-800" style={{ filter: `blur(${(p.intensity || 12) / 4}px)` }} />
                        ) : (
                          <div className="w-full h-full bg-slate-800 grid place-items-center text-white/50">
                            <VideoOff size={16} />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Professional category */}
                <div className="text-[11px] font-semibold text-white/70 uppercase tracking-wider mb-2">Professional</div>
                <div className="grid grid-cols-4 gap-2 max-h-[300px] overflow-y-auto pr-1">
                  {BG_PRESETS.filter(p => p.category === "Professional").map(p => {
                    const on = background.id === p.id;
                    return (
                      <button
                        key={p.id}
                        onClick={() => { setBackground(p); }}
                        data-testid={`meeting-bg-${p.id}`}
                        className={`relative rounded-lg overflow-hidden border-2 transition ${on ? "border-[#3b82f6] ring-2 ring-[#3b82f6]/40" : "border-white/10 hover:border-white/30"}`}
                        style={{ aspectRatio: "1/1" }}
                        title={p.label}
                      >
                        <img src={p.src} alt={p.label} loading="lazy" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.opacity = "0.15"; }} />
                      </button>
                    );
                  })}
                </div>

                <div className="text-[9px] text-white/40 mono mt-2 tracking-widest">
                  // {background.category === "Effects" ? (background.id === "none" ? "no effect" : "css blur applied") : "ai segmentation composites this image behind you"}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="w-full md:w-80 md:border-l border-t md:border-t-0 border-white/10 bg-black/60 flex flex-col md:shrink-0 min-h-0 max-h-[40vh] md:max-h-none overflow-y-auto">
          {/* Participants list — always visible at the top of the sidebar */}
          <div className="border-b border-white/10">
            <div className="px-3 py-2 flex items-center justify-between text-xs uppercase tracking-widest text-white/40">
              <div className="flex items-center gap-1.5">
                <Users size={12} color="#3b82f6" /> Participants ({tiles.length})
              </div>
              {isHost && Object.keys(remotePeers).length > 0 && (
                <button
                  onClick={hostMuteAll}
                  data-testid="meeting-host-muteall"
                  className="text-[9px] mono uppercase tracking-widest bg-red-500/25 text-red-300 hover:bg-red-500/40 px-2 py-0.5 rounded-full flex items-center gap-1"
                  title="Mute every participant (host)"
                >
                  <ShieldCheck size={9} /> mute all
                </button>
              )}
            </div>
            <div className="max-h-56 overflow-y-auto px-2 pb-2 space-y-1" data-testid="meeting-participants-list">
              {tiles.map((t) => {
                const raised = !!handsRaised[t.key];
                const canHostAct = isHost && !t.isLocal;
                return (
                  <div key={t.key} data-testid={`meeting-participant-${t.key}`} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/5 group">
                    <div className="w-7 h-7 rounded-full text-[10px] font-bold flex items-center justify-center shrink-0 relative" style={{ background: `${t.color}30`, color: t.color }}>
                      {(t.name || "?").slice(0, 1).toUpperCase()}
                      {raised && (
                        <span className="absolute -top-1 -right-1 text-[11px]" title="Hand raised">✋</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] text-white/90 truncate flex items-center gap-1">
                        {t.name}
                        {t.isLocal && <span className="text-[9px] mono text-white/40">· you</span>}
                        {t.isLocal && isHost && <span className="text-[9px] mono text-emerald-400/70 uppercase tracking-widest">· host</span>}
                      </div>
                      <div className="text-[9px] mono text-white/40 uppercase tracking-widest">
                        {raised ? "hand raised" : t.stream ? "connected" : "joining…"}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {t.stream?.getVideoTracks?.().some(v => v.enabled) ? (
                        <Video size={11} className="text-emerald-400" />
                      ) : (
                        <VideoOff size={11} className="text-white/30" />
                      )}
                      {t.isLocal ? (
                        micOn ? <Mic size={11} className="text-emerald-400" /> : <MicOff size={11} className="text-red-400" />
                      ) : (
                        t.stream?.getAudioTracks?.().some(a => a.enabled) ? (
                          <Mic size={11} className="text-emerald-400" />
                        ) : (
                          <MicOff size={11} className="text-white/30" />
                        )
                      )}
                    </div>
                    {canHostAct && (
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition">
                        <button
                          onClick={() => hostMute(t.key, "audio")}
                          data-testid={`meeting-host-mute-audio-${t.key}`}
                          className="p-1 hover:bg-red-500/25 rounded text-red-300"
                          title="Mute microphone (host)"
                        >
                          <MicOff size={10} />
                        </button>
                        <button
                          onClick={() => hostMute(t.key, "video")}
                          data-testid={`meeting-host-mute-video-${t.key}`}
                          className="p-1 hover:bg-red-500/25 rounded text-red-300"
                          title="Turn off camera (host)"
                        >
                          <VideoOff size={10} />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="px-3 py-2 border-b border-white/10 flex items-center gap-1.5 text-xs uppercase tracking-widest text-white/40">
            <Sparkles size={12} color="#ff7a2e" /> AI Meeting Assistant
          </div>
          <div className="p-3 flex flex-col gap-2 border-b border-white/10">
            <div className="flex items-center justify-between">
              <button type="button" onClick={() => setTranscriptOpen(o => !o)} data-testid="meeting-transcript-toggle" className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-white/40 hover:text-white/70">
                {transcriptOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />} Live Transcript
                {!transcriptOpen && listening && <span className="ml-1 w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" title="Live" />}
              </button>
              <select
                data-testid="meeting-transcript-lang"
                value={transcriptLang}
                onChange={(e) => setTranscriptLang(e.target.value)}
                className="bg-white/5 border border-white/10 rounded px-1.5 py-0.5 text-[10px] text-white/70"
                title="Transcription language (applies next time you start listening)"
              >
                <option value="en-US">English (US)</option>
                <option value="en-IN">English (India)</option>
                <option value="hi-IN">Hindi (India)</option>
              </select>
            </div>
            {transcriptOpen && (<>
            <textarea
              data-testid="meeting-transcript-text"
              value={transcript}
              onChange={(e) => setTranscript(e.target.value)}
              rows={5}
              placeholder="Start transcribing with the sparkle button…"
              className="bg-white/5 border border-white/10 rounded p-2 text-[11px] resize-none mono"
            />
            {interimTranscript && (
              <div data-testid="meeting-transcript-interim" className="text-[11px] text-white/35 italic -mt-1 px-1 truncate">{interimTranscript}</div>
            )}
            {!SR_SUPPORTED && (
              <div className="text-[10px] text-amber-400/80">Chrome or Edge required for live transcription.</div>
            )}
            {transcribeError && (
              <div data-testid="meeting-transcript-error" className="text-[10px] text-red-400/80">{transcribeError}</div>
            )}
            {isHost && (
            <button disabled={aiBusy || !transcript} onClick={genMinutes} data-testid="meeting-ai-minutes" className="btn-accent text-xs flex items-center justify-center gap-1">
              {aiBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Generate Minutes
            </button>
            )}
            {minutesSentInfo && <div data-testid="meeting-minutes-sent" className="text-[10px] text-emerald-400/80">{minutesSentInfo}</div>}
            </>)}
          </div>
          {minutes && (
            <div className="p-3 flex-1 overflow-y-auto">
              <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Meeting Minutes</div>
              <div className="text-xs whitespace-pre-wrap text-white/85">{minutes}</div>
            </div>
          )}
          {chatOpen && (
            <div className="border-t border-white/10 flex flex-col max-h-56">
              <div className="text-[10px] uppercase tracking-widest text-white/40 px-3 py-2 border-b border-white/10">Chat</div>
              <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1 text-xs">
                {chatMsgs.map((m, i) => <div key={i}><span className="text-[#ff7a2e]">{m.from}: </span>{m.text}</div>)}
                {chatMsgs.length === 0 && <div className="text-white/40">No messages.</div>}
              </div>
              <div className="flex gap-1 p-2 border-t border-white/10">
                <input value={chatInput} onChange={(e) => setChatInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendChat()} className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1 text-xs" placeholder="Message…" />
                <button onClick={sendChat} className="btn-accent text-xs">Send</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Tile({ name, color, videoRef, stream, testId, muted, sharing, isLocal, isSpeaking }) {
  const innerRef = useRef(null);
  const ref = videoRef || innerRef;
  useEffect(() => {
    if (ref.current && stream) {
      try { ref.current.srcObject = stream; } catch { /* noop */ }
    }
  }, [stream]);

  return (
    <div data-testid={testId} className={`relative bg-slate-900 border ${sharing ? "border-[#8b5cf6]" : isSpeaking ? "border-emerald-400" : "border-white/10"} rounded-xl overflow-hidden flex items-center justify-center min-h-0 transition-all`}
         style={{ boxShadow: isSpeaking ? "0 0 0 2px rgba(16,185,129,0.55), 0 0 24px rgba(16,185,129,0.35)" : undefined }}>
      {stream ? (
        <video
          ref={ref}
          autoPlay
          playsInline
          muted={muted}
          className={`w-full h-full relative z-10 ${sharing ? "object-contain bg-black" : "object-cover"}`}
          // Mirror local (self-view) video only, like Google Meet / Zoom.
          style={{ transform: isLocal && !sharing ? "scaleX(-1)" : "none" }}
        />
      ) : (
        <div className="w-20 h-20 rounded-full flex items-center justify-center text-2xl font-light" style={{ background: `${color}22`, color }}>
          {(name || "?").slice(0, 1).toUpperCase()}
        </div>
      )}
      {/* Name label — always visible, bottom-left */}
      <div className="absolute bottom-2 left-2 z-20 flex items-center gap-1.5 text-[11px] bg-black/65 backdrop-blur-sm px-2 py-0.5 rounded-full text-white/95">
        {isSpeaking && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />}
        {name}
        {isLocal && <span className="text-white/50">· You</span>}
      </div>
      {sharing && (
        <div className="absolute top-2 right-2 z-20 mono text-[9px] uppercase tracking-widest bg-[#8b5cf6] text-[#0b1014] px-1.5 py-0.5 font-bold rounded">
          screen
        </div>
      )}
    </div>
  );
}
